import {DataFetcherRequest, useGrpcRequest} from '@aboutyou/data-fetcher';
// eslint-disable-next-line jest/no-mocks-import
import {StoryPageService_GetPageLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/story_page/v1/service.grpc.lazy';
import {useBasketTokenAwaitedAnySession} from '@aboutyou/user';
import {useMemo} from 'react';
import {PRODUCTS_PER_PAGE} from '../../../../features/ProductStream/constants';
import {labelToSortingOption} from '../../../../features/ProductStream/helpers';
import {
  useAppliedLiveAtFilter,
  useAppliedPotentialFilters,
  useKeepPreviousData,
  useResetOnFetchEnd,
  useSorting,
} from '../../../../features/ProductStream/utils';
import {useReductionsStateRequestParameter} from '../../../../features/ReductionsState/useReductionsStateRequestParameter';
import {useFirstProductIds} from '../../../../features/firstProductId/useFirstProductIds';
import {useCategoryIdentifier} from '../../../../features/hooks/useCategoryIdentifier';
import {useProtoProductImageType} from '../../../../shared-deprecated/useProductImageType';
import {useTargetGroupIdentifier} from '../../../../shared-deprecated/useTargetGroupIdentifier';
import {useStoryIdFromUrl} from '../../utils/useStoryIdFromUrl';

export const useStoryPageRequestParams = () => {
  const storyId = useStoryIdFromUrl();

  const {sorting} = useSorting();
  const {session, refetchesDisabled} = useBasketTokenAwaitedAnySession();
  const targetGroup = useTargetGroupIdentifier();
  const {keepPreviousData, resetOnFetchEnd} = useKeepPreviousData();
  const appliedFilters = useAppliedPotentialFilters();
  const liveAtFilter = useAppliedLiveAtFilter();
  const firstProductIds = useFirstProductIds();
  const preferredImage = useProtoProductImageType();
  const reductionsState = useReductionsStateRequestParameter();
  const categoryIdentifier = useCategoryIdentifier();

  return {
    params: {
      session,
      storyId: storyId!,
      appliedFilters: {
        filters: appliedFilters,
        liveAt: liveAtFilter
          ? {
              minDate: liveAtFilter,
            }
          : undefined,
      },
      sortOptions: labelToSortingOption(sorting),
      reductionsState,
      firstProductIds,
      preferredProductImageType: preferredImage,
      targetGroup,
      categoryId: categoryIdentifier?.identifier.id ?? null,
      enableAiuta: false,
    },
    disabled: refetchesDisabled || !storyId,
    keepPreviousData,
    resetOnFetchEnd,
  };
};

// `useTokenBasedStreamPages`/`UpdateNextToken` read `data.nextState`, but the story page
// wire type calls the field `continuationToken` - alias it rather than touching the
// shared pagination machinery used by every other stream.
export const useWithNextState = <T extends {continuationToken: string}>(
  request: DataFetcherRequest<T>,
): DataFetcherRequest<T & {nextState?: string}> => {
  const data = useMemo(
    () =>
      request.data
        ? {...request.data, nextState: request.data.continuationToken}
        : request.data,
    [request.data],
  );

  return request.data
    ? ({...request, data} as DataFetcherRequest<T & {nextState?: string}>)
    : (request as DataFetcherRequest<T & {nextState?: string}>);
};

export const useStoryPageStream = () => {
  const {params, disabled, keepPreviousData, resetOnFetchEnd} =
    useStoryPageRequestParams();

  const request = useGrpcRequest(
    StoryPageService_GetPageLazy,
    {
      ...params,
      limit: PRODUCTS_PER_PAGE,
    },
    {
      disabled,
      keepPreviousData,
      retry: 1,
      mockData: async params =>
        import('./__mocks__/getStoryPageMock').then(module =>
          module.getStoryPageMock(params),
        ),
    },
  );

  useResetOnFetchEnd(request, resetOnFetchEnd);

  return useWithNextState(request);
};
